import type { CSSProperties, ReactNode } from "react";

/**
 * Skeleton — the loading-is-the-entrance primitive (KICKOFF §3.3).
 * Renders in the panel's own clipped geometry (clip prop keyed to tokens.clip,
 * default the primary 8px bevel) with a 45° steel shimmer sweep on token timing.
 * CLS-safe: dimensions come from width/height/aspect props — the skeleton
 * reserves exactly the space the real content will occupy, never content-jumps.
 */

/** Keys map 1:1 to the tokens.clip geometries (.mdui-clip-* utility classes). */
export type SkeletonClip =
  | "bevel"
  | "bevel-6"
  | "bevel-7"
  | "bevel-10"
  | "notch"
  | "notch-6"
  | "notch-10"
  | "notch-12"
  | "notch-14"
  | "slant-7"
  | "slant-8"
  | "slant-9"
  | "slant-11"
  | "slant-12"
  | "slant-13"
  | "slant-14"
  | "slant-16"
  | "cut-br-9"
  | "cut-br-10"
  | "none";

export type SkeletonVariant = "panel" | "card" | "text-line" | "stat-row";

export interface SkeletonProps {
  /** Shape of the loading placeholder. Default "panel". */
  variant?: SkeletonVariant;
  /** tokens.clip geometry to render in. Default "bevel" (the primary 8px bevel). */
  clip?: SkeletonClip;
  width?: number | string;
  height?: number | string;
  /** width / height aspect ratio (CLS-safe sizing without a fixed height). */
  aspect?: number;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({
  variant = "panel",
  clip = "bevel",
  width,
  height,
  aspect,
  className,
  style,
}: SkeletonProps) {
  const dims: CSSProperties = {};
  if (width != null) dims.width = width;
  if (height != null) dims.height = height;
  if (aspect != null) dims.aspectRatio = String(aspect);

  const cls = [
    "mdui-skel",
    `mdui-skel--${variant}`,
    clip !== "none" ? `mdui-clip-${clip}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (variant === "stat-row") {
    return (
      <div className={cls} style={{ ...dims, ...style }} aria-hidden="true">
        <span className="mdui-skel-bone mdui-skel-bone--label" />
        <span className="mdui-skel-bone mdui-skel-bone--bar" />
        <span className="mdui-skel-bone mdui-skel-bone--value" />
      </div>
    );
  }

  return <div className={cls} style={{ ...dims, ...style }} aria-hidden="true" />;
}

export interface SkeletonToContentProps {
  /** While true the skeleton shows; when it flips false the children RISE in. */
  loading: boolean;
  /** The skeleton to show while loading (size it to the content it stands in for). */
  skeleton?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Default true: children mount immediately (hidden, inert) so the container
   * takes its final size from first paint and the skeleton overlays it — the
   * swap is opacity/transform only, i.e. ZERO layout shift (CLS law, §3.1).
   * Pass false when content cannot render before its data exists (real fetches,
   * Loop 1+); in that mode the skeleton MUST be metric-matched to the content
   * it stands in for, or the swap will shift everything below it.
   */
  reserve?: boolean;
}

/**
 * SkeletonToContent — swaps skeleton -> children with the RISE entrance grammar
 * (translateY 24px -> 0 + opacity, E-enter/settle, 450ms). Real content arrives
 * as choreography: the same motion grammar as the show starting, so the player
 * never distinguishes "loading finished" from "the screen performed its entrance".
 *
 * SLOW fix (Lighthouse gate, 22 Jul 2026): the original conditional swap let a
 * mismatched skeleton shift the whole page (CLS 0.20 on the landing route).
 * Reserve mode pins the container to the content's own size from first paint.
 *
 * JUICER SEAM: when packages/juice ships its RISE primitive, this wrapper should
 * adopt it (replace the .mdui-rise class/keyframe with the juice implementation)
 * so entrance choreography has a single owner.
 */
export function SkeletonToContent({
  loading,
  skeleton,
  children,
  className,
  reserve = true,
}: SkeletonToContentProps) {
  if (!reserve) {
    if (loading) {
      return <>{skeleton ?? <Skeleton variant="panel" />}</>;
    }
    return (
      <div className={["mdui-rise", className].filter(Boolean).join(" ")}>
        {children}
      </div>
    );
  }

  return (
    <div className={["mdui-swap", className].filter(Boolean).join(" ")}>
      <div
        className={loading ? "mdui-swap-content" : "mdui-swap-content mdui-rise"}
        data-state={loading ? "loading" : "shown"}
        aria-hidden={loading || undefined}
      >
        {children}
      </div>
      {loading ? (
        <div className="mdui-swap-skel">{skeleton ?? <Skeleton variant="panel" height="100%" />}</div>
      ) : null}
    </div>
  );
}
